/**
 * The front desk PIN.
 *
 * Hashed with PBKDF2-SHA256 and a per-install random salt, using Web Crypto so
 * it works with no network. Verification is a local computation — the PIN
 * screen behaves identically online and offline.
 *
 * Be clear-eyed about what this is: there are only 10,000 four-digit PINs, so
 * anyone with the machine and a few seconds can brute-force the stored hash.
 * This stops a passer-by or a curious member touching an unattended front desk.
 * It is NOT what protects the data — Supabase Row Level Security is. Do not
 * treat the PIN as a security boundary.
 */
import { now } from '@shared/dates';
import { getSetting, setSetting, SETTING_KEYS } from '../db/db';

const ITERATIONS = 210_000;
const KEY_LENGTH_BITS = 256;

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function fromHex(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

async function derive(pin: string, salt: Uint8Array): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits']
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: ITERATIONS, hash: 'SHA-256' },
    key,
    KEY_LENGTH_BITS
  );
  return toHex(bits);
}

/** Length-independent comparison, so timing does not leak the hash. */
function constantTimeEquals(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

export function isValidPinFormat(pin: string): boolean {
  return /^\d{4}$/.test(pin);
}

/**
 * Two separate PINs.
 *
 *   staff    unlocks the app. Everyone on the front desk knows it.
 *   manager  unlocks Settings — prices, the payout split, coach assignments
 *            and the PINs themselves. Front desk staff are NOT given this one.
 *
 * They are stored under different keys with different salts, so knowing one
 * tells you nothing about the other.
 */
export type PinKind = 'staff' | 'manager';

const KEYS: Record<PinKind, { hash: string; salt: string }> = {
  staff: { hash: SETTING_KEYS.pinHash, salt: SETTING_KEYS.pinSalt },
  manager: { hash: SETTING_KEYS.managerPinHash, salt: SETTING_KEYS.managerPinSalt },
};

export async function isPinSet(kind: PinKind = 'staff'): Promise<boolean> {
  return (await getSetting(KEYS[kind].hash)) !== null;
}

export async function setPin(pin: string, kind: PinKind = 'staff'): Promise<void> {
  if (!isValidPinFormat(pin)) throw new Error('The PIN must be exactly 4 digits');

  // Refusing to let the two match is worth the friction: an identical manager
  // PIN would hand every staff member the keys to the price list.
  if (kind === 'manager' && (await isPinSet('staff')) && (await checkPin(pin, 'staff'))) {
    throw new Error('The manager PIN must be different from the staff PIN');
  }

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hash = await derive(pin, salt);
  await setSetting(KEYS[kind].salt, toHex(salt.buffer));
  await setSetting(KEYS[kind].hash, hash);
}

/**
 * The raw hash comparison, with NO throttle. Only for internal checks that are
 * not a person guessing — `setPin` comparing a new manager PIN to the staff one
 * must not count as a failed attempt.
 */
async function checkPin(pin: string, kind: PinKind): Promise<boolean> {
  if (!isValidPinFormat(pin)) return false;
  const [saltHex, expected] = await Promise.all([
    getSetting(KEYS[kind].salt),
    getSetting(KEYS[kind].hash),
  ]);
  if (!saltHex || !expected) return false;
  const actual = await derive(pin, fromHex(saltHex));
  return constantTimeEquals(actual, expected);
}

/* -------------------------------------------------------------------------
   Brute-force throttle
   ------------------------------------------------------------------------- */

/** Wrong guesses allowed before the first lock. */
export const FREE_ATTEMPTS = 5;
export const BASE_LOCK_MS = 30_000;
export const MAX_LOCK_MS = 15 * 60_000;

const THROTTLE_KEYS: Record<PinKind, string> = {
  staff: SETTING_KEYS.pinThrottleStaff,
  manager: SETTING_KEYS.pinThrottleManager,
};

interface ThrottleState {
  failures: number;
  lockedUntilMs: number;
}

export type PinAttempt =
  | { ok: true }
  | { ok: false; lockedUntilMs?: number; remainingAttempts?: number };

async function readThrottle(kind: PinKind): Promise<ThrottleState> {
  const raw = await getSetting(THROTTLE_KEYS[kind]);
  if (!raw) return { failures: 0, lockedUntilMs: 0 };
  try {
    const parsed = JSON.parse(raw) as Partial<ThrottleState>;
    const failures = Number(parsed.failures);
    const lockedUntilMs = Number(parsed.lockedUntilMs);
    return {
      failures: Number.isFinite(failures) && failures > 0 ? Math.floor(failures) : 0,
      lockedUntilMs: Number.isFinite(lockedUntilMs) ? lockedUntilMs : 0,
    };
  } catch {
    // A corrupt record must never become a permanent lockout.
    return { failures: 0, lockedUntilMs: 0 };
  }
}

function writeThrottle(kind: PinKind, state: ThrottleState): Promise<void> {
  return setSetting(THROTTLE_KEYS[kind], JSON.stringify(state));
}

/**
 * A lock further away than the cap can only come from the clock having been
 * wound back since it was written. Clamping keeps the lock temporary by
 * construction — there is no state from which waiting does not clear it.
 */
function effectiveLockEnd(state: ThrottleState, nowMs: number): number {
  return Math.min(state.lockedUntilMs, nowMs + MAX_LOCK_MS);
}

function lockDurationMs(failures: number): number {
  if (failures < FREE_ATTEMPTS) return 0;
  const doublings = Math.min(failures - FREE_ATTEMPTS, 10);
  return Math.min(BASE_LOCK_MS * 2 ** doublings, MAX_LOCK_MS);
}

/** When this kind unlocks (epoch ms), or null if it is not locked right now. */
export async function getPinLockedUntil(kind: PinKind): Promise<number | null> {
  const nowMs = now().getTime();
  const end = effectiveLockEnd(await readThrottle(kind), nowMs);
  return end > nowMs ? end : null;
}

/** Wrong guesses left before a lock starts, or null once the allowance is spent. */
export async function getRemainingAttempts(kind: PinKind): Promise<number | null> {
  const { failures } = await readThrottle(kind);
  return failures < FREE_ATTEMPTS ? FREE_ATTEMPTS - failures : null;
}

// Attempts run one at a time: two concurrent guesses must not both slip past a
// lock that the first one is about to set.
let queue: Promise<unknown> = Promise.resolve();

/**
 * Check a PIN typed by a person. EVERY such check goes through here. While the
 * kind is locked it refuses without running PBKDF2, even for the right PIN.
 */
export function attemptPin(pin: string, kind: PinKind = 'staff'): Promise<PinAttempt> {
  const run = queue.then(() => attemptPinNow(pin, kind));
  queue = run.catch(() => undefined);
  return run;
}

async function attemptPinNow(pin: string, kind: PinKind): Promise<PinAttempt> {
  const nowMs = now().getTime();
  const state = await readThrottle(kind);
  const lockEnd = effectiveLockEnd(state, nowMs);
  if (lockEnd > nowMs) return { ok: false, lockedUntilMs: lockEnd };

  if (await checkPin(pin, kind)) {
    if (state.failures !== 0 || state.lockedUntilMs !== 0) {
      await writeThrottle(kind, { failures: 0, lockedUntilMs: 0 });
    }
    return { ok: true };
  }

  const failures = state.failures + 1;
  const wait = lockDurationMs(failures);
  const lockedUntilMs = wait > 0 ? now().getTime() + wait : 0;
  await writeThrottle(kind, { failures, lockedUntilMs });
  return wait > 0
    ? { ok: false, lockedUntilMs }
    : { ok: false, remainingAttempts: FREE_ATTEMPTS - failures };
}

export function secondsUntil(lockedUntilMs: number): number {
  return Math.max(1, Math.ceil((lockedUntilMs - now().getTime()) / 1000));
}

export function lockMessage(lockedUntilMs: number): string {
  return `Too many wrong PINs — try again in ${secondsUntil(lockedUntilMs)} s`;
}

/** Throttled boolean check, for callers that only need yes/no. */
export async function verifyPin(pin: string, kind: PinKind = 'staff'): Promise<boolean> {
  return (await attemptPin(pin, kind)).ok;
}

/** Requires the current PIN, so a passer-by cannot lock staff out. */
export async function changePin(
  currentPin: string,
  newPin: string,
  kind: PinKind = 'staff'
): Promise<void> {
  const attempt = await attemptPin(currentPin, kind);
  if (!attempt.ok) {
    throw new Error(
      attempt.lockedUntilMs !== undefined
        ? lockMessage(attempt.lockedUntilMs)
        : 'Current PIN is incorrect'
    );
  }
  await setPin(newPin, kind);
}
